import React, { useMemo, useState } from "react";
import { RoundTripFlightMatch } from "../../models/RoundTripFlightMatch";
import { Flight } from "../../models/Flight";
import { useTranslation } from "../../i18n/LanguageProvider";

import RoundTripListItem from "../list/RoundTripListItem";
import {getImageUrl} from "../../utils/GetImageUrl";

interface Props {
  flightList: RoundTripFlightMatch[];
  dateFrom: Date | null;
  dateTo: Date | null;
}

interface CalendarDay {
  date: Date;
  key: string;
  isCurrentMonth: boolean;
}

interface DayFlightGroup {
  dateKey: string;
  flights: RoundTripFlightMatch[];
  cheapestPrice: number;
  bestFlight: RoundTripFlightMatch;
}

const padDatePart = (value: number) => String(value).padStart(2, "0");

const getDateKey = (date: Date | string) => {
  if (typeof date === "string") return date;
  return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;
};

const getMonthStart = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);
const getMonthEnd = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);

const addDays = (date: Date, days: number) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

const getCalendarDays = (monthDate: Date): CalendarDay[] => {
  const monthStart = getMonthStart(monthDate);
  const monthEnd = getMonthEnd(monthDate);
  const calendarStart = addDays(monthStart, -((monthStart.getDay() + 6) % 7));
  const calendarEnd = addDays(monthEnd, 6 - ((monthEnd.getDay() + 6) % 7));
  const days: CalendarDay[] = [];

  for (let date = calendarStart; date <= calendarEnd; date = addDays(date, 1)) {
    days.push({
      date,
      key: getDateKey(date),
      isCurrentMonth: date.getMonth() === monthDate.getMonth(),
    });
  }

  return days;
};

const getMonthsBetween = (dateFrom: Date | null, dateTo: Date | null, flightList: RoundTripFlightMatch[]) => {
  const flightDates = flightList.map(
    (flight) => new Date(`${flight.round_trip_flight.outbound_flight.departure_date}T00:00:00`)
  );
  const start = dateFrom ?? flightDates[0] ?? new Date();
  const end = dateTo ?? flightDates[flightDates.length - 1] ?? start;
  const months = [];
  let current = getMonthStart(start);
  const last = getMonthStart(end);

  while (current <= last) {
    months.push(new Date(current));
    current = new Date(current.getFullYear(), current.getMonth() + 1, 1);
  }

  return months;
};

const getLegs = (flight: RoundTripFlightMatch): Flight[] => {
  const roundTripFlight = flight.round_trip_flight;
  return roundTripFlight.outbound_flights?.length
    ? roundTripFlight.outbound_flights
    : [roundTripFlight.outbound_flight];
};

const hasEstimatedPrice = (flight: RoundTripFlightMatch) =>
  getLegs(flight).some((leg) => leg.price_is_estimated);

const hasMixedCurrencies = (flight: RoundTripFlightMatch) =>
  new Set(getLegs(flight).map((leg) => leg.currency)).size > 1;

const formatTime = (time: string) => time.split(":").slice(0, 2).join(":");

const formatRoute = (legs: Flight[]) =>
  legs.map((leg) => `${leg.departure_iata} → ${leg.destination_iata}`).join(" · ");

const formatFlightTime = (legs: Flight[]) => {
  const firstLeg = legs[0];
  const finalLeg = legs[legs.length - 1];
  return `${formatTime(firstLeg.departure_time)}-${formatTime(finalLeg.arrival_time)}`;
};

const OneWayFlightCalendarView: React.FC<Props> = ({ flightList, dateFrom, dateTo }) => {
  const { locale, t } = useTranslation();
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedDestinationIata, setSelectedDestinationIata] = useState<string | null>(null);
  const formatEuroPrice = (price: number, isEstimated = false) =>
    `${isEstimated ? `${t("flight.fromPrice")} ` : ""}${price.toFixed(0)}€`;
  const formatFlightPrice = (flight: RoundTripFlightMatch) => {
    return formatEuroPrice(
      flight.round_trip_flight.total_price,
      hasEstimatedPrice(flight)
    );
  };
  const destinationFilters = useMemo(() => {
    const destinations = flightList.reduce<
      Record<string, { destinationIata: string; destinationName: string; cheapestPrice: number; cheapestFlight: RoundTripFlightMatch }>
    >((groups, flight) => {
      const destinationIata = flight.round_trip_flight.outbound_flight.destination_iata;
      if (!groups[destinationIata]) {
        groups[destinationIata] = {
          destinationIata,
          destinationName: flight.round_trip_flight.destination_city,
          cheapestPrice: flight.round_trip_flight.total_price,
          cheapestFlight: flight,
        };
      }

      if (flight.round_trip_flight.total_price < groups[destinationIata].cheapestPrice) {
        groups[destinationIata].cheapestPrice = flight.round_trip_flight.total_price;
        groups[destinationIata].cheapestFlight = flight;
      }
      return groups;
    }, {});

    return Object.values(destinations).sort((a, b) => {
      const priceDiff = a.cheapestPrice - b.cheapestPrice;
      if (priceDiff !== 0) return priceDiff;
      return a.destinationName.localeCompare(b.destinationName);
    });
  }, [flightList]);

  const filteredFlightList = selectedDestinationIata
    ? flightList.filter(
        (flight) => flight.round_trip_flight.outbound_flight.destination_iata === selectedDestinationIata
      )
    : flightList;

  const groupedByDate = filteredFlightList.reduce<Record<string, DayFlightGroup>>((groups, flight) => {
    const dateKey = getDateKey(flight.round_trip_flight.outbound_flight.departure_date);
    if (!groups[dateKey]) {
      groups[dateKey] = {
        dateKey,
        flights: [],
        cheapestPrice: flight.round_trip_flight.total_price,
        bestFlight: flight,
      };
    }

    groups[dateKey].flights.push(flight);
    if (flight.round_trip_flight.total_price < groups[dateKey].cheapestPrice) {
      groups[dateKey].cheapestPrice = flight.round_trip_flight.total_price;
      groups[dateKey].bestFlight = flight;
    }
    return groups;
  }, {});

  const dayGroups = Object.values(groupedByDate);
  const sortedPrices = dayGroups.map((group) => group.cheapestPrice).sort((a, b) => a - b);
  const greenLimit = sortedPrices[Math.floor((sortedPrices.length - 1) * 0.34)] ?? 0;
  const orangeLimit = sortedPrices[Math.floor((sortedPrices.length - 1) * 0.67)] ?? greenLimit;
  const months = getMonthsBetween(dateFrom, dateTo, flightList);
  const weekdayLabels = Array.from({ length: 7 }, (_, index) => {
    const date = new Date(2026, 5, index + 1);
    return date.toLocaleDateString(locale, { weekday: "short" });
  });

  const getPriceClass = (price: number) => {
    if (price <= greenLimit) return "bg-emerald-50 text-emerald-700 ring-emerald-200 group-hover/day:border-emerald-300";
    if (price <= orangeLimit) return "bg-orange-50 text-orange-700 ring-orange-200 group-hover/day:border-orange-300";
    return "bg-red-50 text-red-700 ring-red-200 group-hover/day:border-red-300";
  };

  return (
    <div className="flex flex-col gap-7 transition-smooth">
      {destinationFilters.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl bg-white p-2 shadow-card ring-1 ring-[#F2F0FA]">
          <button
            onClick={() => setSelectedDestinationIata(null)}
            className={`h-9 rounded-xl px-4 text-xs font-extrabold transition-smooth ${
              selectedDestinationIata === null
                ? "bg-[#712FFF] text-white shadow-sm"
                : "bg-[#F8F7FC] text-[#8C82A8] hover:bg-[#F4F2FF]"
            }`}
          >
            {t("resultsCalendar.allDestinations")}
          </button>
          {destinationFilters.map((destination) => (
            <button
              key={destination.destinationIata}
              onClick={() => setSelectedDestinationIata(destination.destinationIata)}
              className={`flex h-9 items-center gap-2 rounded-xl px-3 text-xs font-extrabold transition-smooth ${
                selectedDestinationIata === destination.destinationIata
                  ? "bg-[#712FFF] text-white shadow-sm"
                  : "bg-[#F8F7FC] text-[#8C82A8] hover:bg-[#F4F2FF]"
              }`}
              title={destination.destinationName}
            >
              <span className="max-w-[140px] truncate">{destination.destinationName}</span>
              <span className={selectedDestinationIata === destination.destinationIata ? "text-white/70" : "text-[#BEBEBE]"}>
                {formatFlightPrice(destination.cheapestFlight)}
              </span>
            </button>
          ))}
        </div>
      )}

      {months.map((month) => (
        <section key={month.toISOString()} className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-3 items-baseline justify-between px-1">
            <h3 className="text-xl font-extrabold tracking-tight text-[#2D2D2D]">
              {month.toLocaleDateString(locale, { month: "long", year: "numeric" })}
            </h3>
            <div className="flex items-center gap-2 text-xs font-bold text-[#BEBEBE]">
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
              <span>{t("resultsCalendar.cheap")}</span>
              <span className="h-2.5 w-2.5 rounded-full bg-orange-400" />
              <span>{t("resultsCalendar.medium")}</span>
              <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
              <span>{t("resultsCalendar.high")}</span>
            </div>
          </div>

          <div className="rounded-2xl bg-white p-2 sm:p-4 shadow-card">
            <div className="grid grid-cols-7 gap-1 sm:gap-2 pb-2">
              {weekdayLabels.map((weekday) => (
                <div key={weekday} className="text-center text-xs font-extrabold text-[#BEBEBE]">
                  {weekday}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1 sm:gap-2">
              {getCalendarDays(month).map((day) => {
                const group = groupedByDate[day.key];
                const sortedFlights = group
                  ? [...group.flights].sort(
                      (a, b) => a.round_trip_flight.total_price - b.round_trip_flight.total_price
                    )
                  : [];
                const bestFlight = sortedFlights[0];
                const bestFlightLegs = bestFlight ? getLegs(bestFlight) : [];
                const bestDestination = bestFlight?.round_trip_flight.destination_city ?? "";
                const bestDestinationIata = bestFlight?.round_trip_flight.outbound_flight.destination_iata ?? "";
                const priceClass = group ? getPriceClass(group.cheapestPrice) : "";

                return (
                  <button
                    type="button"
                    disabled={!group}
                    aria-pressed={selectedDay === day.key}
                    aria-label={`${day.date.toLocaleDateString(locale, {dateStyle: "full"})}${group ? `, ${formatFlightPrice(group.bestFlight)}, ${t("flight.available", {count: group.flights.length})}` : ""}`}
                    onClick={() => setSelectedDay(selectedDay === day.key ? null : day.key)}
                    key={day.key}
                    className={`group/day relative min-w-0 text-left min-h-[80px] sm:min-h-[104px] rounded-xl border border-[#F2F0FA] p-1 sm:p-2 transition-smooth ${
                      selectedDay === day.key ? "bg-[#F4F2FF] ring-2 ring-accent" : day.isCurrentMonth ? "bg-white" : "bg-[#FAFAFD] opacity-50"
                    }`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-1">
                      <span className="text-xs font-bold text-[#9E9E9E]">
                        {day.date.getDate()}
                      </span>
                      {group && group.flights.length > 1 && (
                        <span className="rounded-full bg-[#F4F2FF] px-1.5 py-0.5 text-[10px] font-extrabold text-accent">
                          +{group.flights.length - 1}
                        </span>
                      )}
                    </div>

                    {group && (
                      <>
                        <div
                          className={`mt-2 flex min-h-[48px] sm:min-h-[58px] flex-col justify-center rounded-lg px-0.5 sm:px-2 text-center ring-1 ${priceClass}`}
                        >
                          <span className="text-[11px] font-extrabold uppercase leading-tight">
                            {bestDestinationIata}
                          </span>
                          <span className="hidden sm:block mt-0.5 truncate text-[10px] font-bold opacity-75">
                            {bestDestination}
                          </span>
                          <span className="mt-1 break-words text-[10px] sm:text-sm font-extrabold leading-tight">
                            {formatFlightPrice(group.bestFlight)}
                          </span>
                        </div>
                        <div className="hidden sm:block mt-1 truncate text-center text-[10px] font-bold text-[#BEBEBE]">
                          {bestFlightLegs.length > 0 && formatRoute(bestFlightLegs)}
                        </div>

                        <div className="hidden lg:block pointer-events-none absolute left-1/2 top-[calc(100%+8px)] z-20 w-72 -translate-x-1/2 rounded-2xl bg-[#2D2D2D] p-4 text-white opacity-0 shadow-2xl transition-all duration-200 group-hover/day:opacity-100">
                          <div className="mb-3 flex items-baseline justify-between">
                            <span className="text-sm font-extrabold">
                              {day.date.toLocaleDateString(locale, { day: "2-digit", month: "long" })}
                            </span>
                            <span className="text-xs font-bold text-white/60">
                              {formatFlightPrice(group.bestFlight)}
                            </span>
                          </div>
                          <div className="flex flex-col gap-2">
                            {sortedFlights.slice(0, 4).map((flight, index) => {
                              const legs = getLegs(flight);
                              return (
                                <div key={`${day.key}-${index}`} className="rounded-xl bg-white/10 px-3 py-2">
                                  <div className="flex items-center justify-between gap-3">
                                    <span className="text-xs font-extrabold text-white">
                                      {formatRoute(legs)}
                                    </span>
                                    <span className="text-xs font-extrabold text-emerald-200">
                                      {formatFlightPrice(flight)}
                                    </span>
                                  </div>
                                  <div className="mt-1 text-[11px] font-bold text-white/60">
                                    {formatFlightTime(legs)}
                                    {legs.length > 1 ? ` · ${t("connection.oneStop")}` : ` · ${t("connection.direct")}`}
                                    {hasMixedCurrencies(flight) ? ` · ${t("flight.mixedCurrencyHint")}` : ""}
                                    {hasEstimatedPrice(flight) ? ` · ${t("flight.estimatedPriceHint")}` : ""}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          {selectedDay && selectedDay.startsWith(`${month.getFullYear()}-${padDatePart(month.getMonth() + 1)}`) && groupedByDate[selectedDay] && (
            <div className="flex flex-col gap-3" aria-live="polite">
              <h4 className="font-bold text-primary">
                {new Date(`${selectedDay}T00:00:00`).toLocaleDateString(locale, {dateStyle: "full"})}
              </h4>
              {[...groupedByDate[selectedDay].flights]
                .sort((a, b) => a.round_trip_flight.total_price - b.round_trip_flight.total_price)
                .map((flight, index) => (
                  <RoundTripListItem key={`${selectedDay}-${index}`} roundTripFlightMatch={flight}
                    imageUrl={getImageUrl(flight.round_trip_flight.outbound_flight.destination_iata)} />
                ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
};

export default OneWayFlightCalendarView;
